import { LocationInfo } from '@ixicare/resources';
import { startOfDay } from 'date-fns';
import { parseDate } from '~utils/date';
import { AlertEventKind } from '~components/AlertLogs/AlertEventIcon';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;

export const TIMELINE_SPANS = [1, 2, 4, 8, 12, 24].map((hours) => hours * HOUR);
/** Zoom goes below the shortest preset, down to a few minutes around a moment. */
export const ZOOM_SPANS = [5 * MINUTE, 15 * MINUTE, 30 * MINUTE, ...TIMELINE_SPANS];

// Devices scan every few minutes (every few seconds during an alarm), so "no data" is judged against the
// recent rhythm rather than a fixed period. The clamp keeps one slow scan after an alarm from reading as a
// gap, and an hour of regular scans from hiding a real one.
const GAP_FACTOR = 2.5;
const MIN_GAP = 5 * MINUTE;
const MAX_GAP = 10 * MINUTE;
const RHYTHM_SAMPLES = 5;

export const INDOOR_COLOR = '#4286f3';
export const INDOOR_ALT_COLOR = '#6e9ff6';
export const OUTDOOR_COLOR = '#16b5b2';

export type TimelinePoint = {
  time: number;
  location: LocationInfo;
  isOutdoor: boolean;
  placeKey: string;
};

export type TimelineEvent = {
  time: number;
  kind: AlertEventKind;
  color: string;
  text: string;
};

export type TimelineBand = {
  start: number;
  end: number;
  color: string;
};

export type TimelineSpan = {
  start: number;
  end: number;
};

export type TimelineGap = {
  start: number;
  end: number;
  /** Still silent now; its length says nothing about the window being viewed. */
  ongoing?: boolean;
};

export type TimelineRun = {
  start: number;
  end: number;
  points: TimelinePoint[];
  isOutdoor: boolean;
  label: string;
};

export function toTimelinePoints(locations: LocationInfo[]): TimelinePoint[] {
  const byTime = new Map<number, TimelinePoint>();
  locations.forEach((location) => {
    const time = parseDate(location.timestamp)?.getTime();
    if (time === undefined || Number.isNaN(time)) return;
    const isOutdoor = location.discriminator !== 'INDOOR';
    const placeKey = isOutdoor
      ? 'outdoor'
      : (location.zoneId ?? location.planeUuid ?? location.description ?? 'indoor');
    byTime.set(time, { time, location, isOutdoor, placeKey });
  });
  return [...byTime.values()].sort((a, b) => a.time - b.time);
}

function gapThreshold(points: TimelinePoint[], index: number) {
  const intervals: number[] = [];
  for (let i = index; i > 0 && intervals.length < RHYTHM_SAMPLES; i--) {
    intervals.push(points[i].time - points[i - 1].time);
  }
  if (!intervals.length) return MAX_GAP;
  const sorted = intervals.sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  return Math.min(MAX_GAP, Math.max(MIN_GAP, median * GAP_FACTOR));
}

/** Stretches without a position, including the one still running up to `now`. */
export function findGaps(points: TimelinePoint[], now: number): TimelineGap[] {
  const gaps: TimelineGap[] = [];
  for (let i = 1; i < points.length; i++) {
    if (points[i].time - points[i - 1].time > gapThreshold(points, i - 1)) {
      gaps.push({ start: points[i - 1].time, end: points[i].time });
    }
  }
  const last = points.length - 1;
  if (last >= 0 && now - points[last].time > gapThreshold(points, last)) {
    gaps.push({ start: points[last].time, end: now, ongoing: true });
  }
  return gaps;
}

export function pointAt(points: TimelinePoint[], time: number): TimelinePoint | undefined {
  let low = 0;
  let high = points.length - 1;
  let found: TimelinePoint | undefined;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (points[mid].time <= time) {
      found = points[mid];
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return found;
}

export function gapAt(gaps: TimelineGap[], time: number) {
  return gaps.find((gap) => gap.start < time && time <= gap.end);
}

export function isWithin(windows: TimelineSpan[], time: number) {
  return windows.some((window) => window.start <= time && time <= window.end);
}

/** The parts of each gap that fall inside a window; outside them the track is hidden, not silent. */
export function clipGaps(gaps: TimelineGap[], windows: TimelineSpan[]): TimelineGap[] {
  return gaps.flatMap((gap) =>
    windows
      .filter((window) => window.end > gap.start && window.start < gap.end)
      .map((window) => ({
        start: Math.max(gap.start, window.start),
        end: Math.min(gap.end, window.end),
        ongoing: gap.ongoing && window.end >= gap.end,
      }))
  );
}

/** The stretches between `from` and `to` that no window covers. */
export function outsideWindows(windows: TimelineSpan[], from: number, to: number): TimelineSpan[] {
  const result: TimelineSpan[] = [];
  let cursor = from;
  [...windows]
    .sort((a, b) => a.start - b.start)
    .forEach((window) => {
      if (window.end <= cursor || window.start >= to) return;
      if (window.start > cursor) result.push({ start: cursor, end: window.start });
      cursor = window.end;
    });
  if (cursor < to) result.push({ start: cursor, end: to });
  return result;
}

/** Consecutive positions in the same place, split wherever a gap interrupts them. */
export function groupRuns(points: TimelinePoint[], gaps: TimelineGap[], outdoorLabel: string): TimelineRun[] {
  const gapStarts = new Set(gaps.map((gap) => gap.start));
  const runs: TimelineRun[] = [];
  points.forEach((point, i) => {
    const run = runs[runs.length - 1];
    const previous = points[i - 1];
    if (run && previous.placeKey === point.placeKey && !gapStarts.has(previous.time)) {
      run.points.push(point);
      run.end = point.time;
      return;
    }
    // A run lasts until the next place was reported, unless the device went silent in between.
    if (run && previous && !gapStarts.has(previous.time)) run.end = point.time;
    runs.push({
      start: point.time,
      end: point.time,
      points: [point],
      isOutdoor: point.isOutdoor,
      label: point.isOutdoor ? outdoorLabel : (point.location.description ?? ''),
    });
  });
  return runs;
}

export function tickStep(span: number) {
  if (span <= 7 * MINUTE) return MINUTE;
  if (span <= 20 * MINUTE) return 2 * MINUTE;
  if (span <= 45 * MINUTE) return 5 * MINUTE;
  if (span <= 1.5 * HOUR) return 10 * MINUTE;
  if (span <= 3 * HOUR) return 30 * MINUTE;
  if (span <= 6 * HOUR) return HOUR;
  if (span <= 12 * HOUR) return 2 * HOUR;
  return 3 * HOUR;
}

export function ticks(start: number, end: number): number[] {
  const step = tickStep(end - start);
  const result: number[] = [];
  let time = startOfDay(start).getTime();
  while (time <= end) {
    if (time >= start) result.push(time);
    time += step;
  }
  return result;
}

export function pointColor(point: TimelinePoint) {
  return point.isOutdoor ? OUTDOOR_COLOR : INDOOR_COLOR;
}
